import { useEffect, useState } from 'react';
import { KONTROL_PERIYOT_GUN, KONTROL_SARI_GUN, type KontrolDurumu } from '@mod/domain';
import { metrajGoster } from '../lib/olcu';
import { api } from '../lib/api';
import { DuzenOzeti, KolonBasi, useTabloDuzeni, type Kolon, type TabloDuzeni } from '../lib/tablo';

/**
 * GL-06 — KURULU İSKELELER (devir dokumani Bolum 6.5).
 *
 * Teslim edilmis ama HENUZ SOKULMEMIS iskeleler. Aya bagli DEGIL, ANLIK.
 *
 * Osman'in kritik ihtiyaci: aylardir sahada duran iskeleleri gorup musteriyi
 * sokume yonlendirmek. Bu yuzden VARSAYILAN siralama EN ESKI KURULAN EN USTTE —
 * sef en riskli/en unutulmusu ilk gorur.
 *
 * v9'da satir basina "Söküm aç" butonu KALDIRILDI: sokum/revize artik iskele
 * DETAYINDAN acilir (tek giris noktasi, tutarlilik).
 *
 * ⚠️ EXCEL BENZERI SUZGEC VE COK KOLONLU SIRALAMA (revizyon 12.08.2026).
 *    Kullanicinin bugun Excel'de yaptigi analiz artik burada yapilabiliyor.
 *    Tipik kullanim: *"Değirmen 5'i seç → o ünitedeki iskeleleri en eski
 *    kontrolden en yeniye sırala."* Bu iki adim TEK duzende ifade edilir;
 *    tek kolonluk siralama bunu karsilamiyordu (bkz. lib/tablo.tsx).
 */

export interface KuruluIs {
  id: string;
  isEmriNo: string | null;
  /** Aynı iş emrindeki farklı iskeleleri ayırt eden numara. */
  iskeleOnayNo: string | null;
  tanim: string;
  unite: string;
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde (pano metriği, aylık kümülatif). */
  esdegerM3: number;
  ilgili: string[];
  kuruluGun: number | null;
  kurulumTarihi: string | null;
  uzunSure: boolean;
  yesilKart: boolean;
  sonKontrolGun: number | null;
  sonKontrolTarihi: string | null;
  kontrolVadesiGecti: boolean;
  kontrolDurumu: KontrolDurumu;
  /** Sayacın değeri: son kontrolden, hiç yapılmadıysa TESLİMDEN beri. */
  vadeGun: number | null;
  /** "Vadesi geçti"den AYRI senaryo — ikisi birlikte de olabilir. */
  hicKontrolYapilmadi: boolean;
}

/** Bugün, `<input type="date">` biçiminde — YEREL, UTC değil. */
const BUGUN = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** İçinde bulunulan yılın 1 Ocak'ı — "yıl başı devir" sayımı. */
const YIL_BASI = () => `${new Date().getFullYear()}-01-01`;

/**
 * VADE RENKLERİ — açık sınıf adları.
 *
 * ⚠️ `text-${durum}` gibi kurulmuş bir ad Tailwind tarafından görülmez ve
 *    üretim derlemesinde hiç üretilmez.
 */
const VADE_RENGI: Record<KontrolDurumu, string> = {
  gecti: 'text-crit font-medium',
  yaklasiyor: 'text-warn font-medium',
  guncel: 'text-wait',
};

const gunTarih = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null;

/**
 * KOLONLAR — sıralama/süzme DEĞERİ ile GÖRÜNEN değer ayrı.
 *
 * ⚠️ Metrajda sıralama `esdegerM3` üzerinden: m³ ve m² aynı sütunda görünüyor
 *    ve ikisini tek eksende kıyaslamanın tanımlı tek yolu eşdeğer (m³ + m²×4).
 *    Görünen değer yine iki ayrı toplam — sayıyı BİRLEŞTİRMİYORUZ, yalnız
 *    sıralarken tek eksene indiriyoruz.
 */
export const KOLONLAR: Kolon<KuruluIs>[] = [
  { anahtar: 'isEmriNo', baslik: 'İş Emri', tip: 'metin', deger: (j) => j.isEmriNo },
  { anahtar: 'iskeleOnayNo', baslik: 'Onay No', tip: 'metin', deger: (j) => j.iskeleOnayNo },
  { anahtar: 'tanim', baslik: 'İş', tip: 'metin', deger: (j) => j.tanim },
  { anahtar: 'unite', baslik: 'Ünite', tip: 'kategori', deger: (j) => j.unite },
  {
    anahtar: 'metraj',
    baslik: 'Metraj',
    tip: 'sayi',
    birim: 'm³ eşd.',
    sagaYasli: true,
    deger: (j) => j.esdegerM3,
  },
  { anahtar: 'kuruluGun', baslik: 'Kurulu süre', tip: 'gun', birim: 'gün', deger: (j) => j.kuruluGun },
  {
    /*
     * ⚠️ SIRALAMA/SÜZME `vadeGun` ÜZERİNDEN, `sonKontrolGun` üzerinden DEĞİL.
     *    Hiç kontrol edilmemiş iskelede ham alan null'dır; sayaç ise teslimden
     *    işler. Ham alanla sıralasaydık "kaç gündür kontrolsüz" sorusu, hiç
     *    kontrol edilmemişlerde cevapsız kalır ve onlar listenin ucuna
     *    savrulurdu — oysa sorunun cevabı onlarda da var.
     */
    anahtar: 'vadeGun',
    baslik: 'Haftalık Kontrol',
    tip: 'gun',
    birim: 'gün',
    deger: (j) => j.vadeGun,
  },
  { anahtar: 'ilgili', baslik: 'İlgili', tip: 'kategori', deger: (j) => j.ilgili[0] ?? '' },
];

/**
 * VARSAYILAN SIRALAMA — §6.5'in tek amacı.
 *
 * ⚠️ "En eski kurulan üstte" bir görünüm tercihi değil, ekranın var oluş
 *    sebebi: *"aylardır sahada duran iskeleyi görüp müşteriyi söküme
 *    yönlendirmek"*. `kuruluGun` BÜYÜK olan daha eski kurulmuştur → `desc`.
 */
const VARSAYILAN_SIRA = [{ anahtar: 'kuruluGun', yon: 'desc' as const }];


/**
 * VADE GÖRÜNÜMLERİ — dashboard kartlarının kurduğu düzen.
 *
 * ⚠️ EŞİKLER @mod/domain'DEN GELİR, burada yazılmaz. "7" ve "2" bir zamanlar
 *    kod tabanında üç ayrı yere elle yazılmıştı; kural tek kaynakta
 *    (kontrol.ts) ve buradaki aralıklar ondan TÜRETİLİR.
 *
 * Kart tıklaması mevcut süzgeç mekanizmasını kullanır — ayrı bir "görünüm"
 * kavramı açmaz. Sonuç özet şeridinde görünür ve normal yoldan temizlenir.
 */
export const VADE_GORUNUMU: Record<'gecti' | 'yaklasiyor', TabloDuzeni> = {
  /** KIRMIZI — sayaç 7 günü bulmuş ya da aşmış. */
  gecti: {
    sira: [{ anahtar: 'vadeGun', yon: 'desc' }],
    suzgecler: {
      vadeGun: { tip: 'aralik', min: KONTROL_PERIYOT_GUN, max: null, bosDahil: false },
    },
  },
  /** SARI — tam olarak 6. gün: "yarın kontrol var". */
  yaklasiyor: {
    sira: [{ anahtar: 'vadeGun', yon: 'desc' }],
    suzgecler: {
      vadeGun: {
        tip: 'aralik',
        min: KONTROL_SARI_GUN,
        max: KONTROL_PERIYOT_GUN - 1,
        bosDahil: false,
      },
    },
  },
};

/**
 * DASHBOARD — Kurulu İskeleler'in üstünde özet panel (revizyon 12.08.2026).
 *
 * İKİ METRİK, ÜÇÜNCÜSÜ YOK. Kullanıcı kapsamı açıkça çizdi: *"Ünite bazlı ayrı
 * dashboard kartları veya ünite gruplaması YAPILMAYACAK. Ünite kırılımı,
 * dashboard'da değil filtre/sıralama üzerinden sağlanacak."* Panelin işi
 * "kaç tane var" demek; "hangi ünitede" sorusunun yeri tablonun kendisi.
 *
 * ⚠️ SAYILAR SÜZGEÇTEN ETKİLENMEZ, tarih seçiminden etkilenir. Panel o günün
 *    TOPLAM tablosudur; kullanıcının o an ne süzdüğüne göre değişseydi
 *    "vadesi geçmiş 12" ile "vadesi geçmiş 3" arasında gidip gelir ve
 *    hatırlatmanın işaret ettiği sayı olmaktan çıkardı.
 */
function VadePaneli({
  isler,
  aktif,
  onSec,
}: {
  isler: KuruluIs[];
  aktif: 'gecti' | 'yaklasiyor' | null;
  onSec: (g: 'gecti' | 'yaklasiyor' | null) => void;
}) {
  const gecti = isler.filter((j) => j.kontrolDurumu === 'gecti').length;
  const yaklasiyor = isler.filter((j) => j.kontrolDurumu === 'yaklasiyor').length;
  /*
   * ⚠️ "Hiç kontrol edilmemiş" AYRI BİR EKSEN, üçüncü bir kart değil.
   *    Kullanıcı iki metrik istedi; ama bu iki senaryonun ayrı olduğunu da
   *    söyledi. Vadesi geçmişlerin kaçının HİÇ kontrol edilmediğini alt
   *    satırda göstermek ikisini ayırır, karta dönüştürmeden.
   */
  const gectiVeHicYapilmadi = isler.filter(
    (j) => j.kontrolDurumu === 'gecti' && j.hicKontrolYapilmadi,
  ).length;

  /*
   * ⚠️ RENK SINIFLARI AÇIK YAZILIR — `text-${renk}` gibi kurulmuş bir sınıf
   *    adı Tailwind tarafından GÖRÜLMEZ ve üretim derlemesinde hiç üretilmez.
   *    Geliştirmede çalışıyor görünüp canlıda renksiz çıkan klasik tuzak.
   */
  const RENK = {
    crit: {
      secili: 'border-crit bg-crit/10 ring-1 ring-crit/30',
      sayi: 'text-crit',
      bag: 'text-crit',
    },
    warn: {
      secili: 'border-warn bg-warn/10 ring-1 ring-warn/30',
      sayi: 'text-warn',
      bag: 'text-warn',
    },
  } as const;

  const kart = (
    anahtar: 'gecti' | 'yaklasiyor',
    sayi: number,
    baslik: string,
    aciklama: string,
    renk: 'crit' | 'warn',
  ) => {
    const secili = aktif === anahtar;
    const r = RENK[renk];
    return (
      <button
        onClick={() => onSec(secili ? null : anahtar)}
        aria-pressed={secili}
        className={`flex-1 rounded-(--radius-mod) border p-4 text-left transition ${
          secili ? r.secili : 'border-line bg-surface hover:border-line-2'
        }`}
      >
        <div className="flex items-baseline gap-2">
          <span className={`num text-3xl font-semibold ${sayi > 0 ? r.sayi : 'text-wait'}`}>
            {sayi}
          </span>
          <span className="text-sm font-medium text-ink-2">{baslik}</span>
        </div>
        <p className="mt-1 text-xs text-wait">{aciklama}</p>
        <p className={`mt-2 text-xs font-medium ${secili ? r.bag : 'text-navy'}`}>
          {secili ? 'Listeyi kapat' : 'Listeyi göster →'}
        </p>
      </button>
    );
  };

  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row">
      {kart(
        'gecti',
        gecti,
        'kontrol vadesi geçmiş',
        gectiVeHicYapilmadi > 0
          ? `${KONTROL_PERIYOT_GUN}+ gündür kontrol edilmedi — ${gectiVeHicYapilmadi} tanesi hiç kontrol edilmemiş`
          : `${KONTROL_PERIYOT_GUN}+ gündür kontrol edilmedi`,
        'crit',
      )}
      {kart(
        'yaklasiyor',
        yaklasiyor,
        'kontrol vadesi yaklaşan',
        `${KONTROL_SARI_GUN}. gün — vadesi yarın doluyor`,
        'warn',
      )}
    </div>
  );
}

/**
 * Aktif vade görünümü DÜZENDEN türetilir, ayrı bir durumda tutulmaz.
 *
 * ⚠️ İki yerde tutulsaydı ayrışırdı: kullanıcı süzgeci başlıktan kaldırdığında
 *    kart hâlâ seçili görünür, sayfa "vadesi geçmiş" diyip tam listeyi
 *    gösterirdi. Tek kaynak düzenin kendisi.
 */
function aktifVade(duzen: TabloDuzeni): 'gecti' | 'yaklasiyor' | null {
  const f = duzen.suzgecler.vadeGun;
  if (f?.tip !== 'aralik') return null;
  for (const [ad, g] of Object.entries(VADE_GORUNUMU) as [
    'gecti' | 'yaklasiyor',
    TabloDuzeni,
  ][]) {
    const b = g.suzgecler.vadeGun;
    if (b?.tip !== 'aralik') continue;
    if (f.min === b.min && f.max === b.max && f.bosDahil === b.bosDahil) return ad;
  }
  return null;
}

export function Kurulu({
  onIsSec,
  baslangicVade,
}: {
  onIsSec: (id: string) => void;
  /**
   * Derin bağlantıdan gelen görünüm (`/kurulu?vade=gecti`).
   *
   * ⚠️ Hatırlatma mekanizması kullanıcıyı BU EKRANA yönlendirecek — tek tek
   *    bildirim yerine toplu liste. Bildirimin bir adresi olması gerekiyor;
   *    burası o adres. (Bildirimin kendisi bu revizyonun kapsamında değil.)
   */
  baslangicVade?: 'gecti' | 'yaklasiyor' | null;
}) {
  const [d, setD] = useState<{ toplamM3: number; isler: KuruluIs[] } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /**
   * HANGİ TARİHTE ayakta olanlar — bir ARALIK değil, bir AN.
   *
   * ⚠️ Fark önemli: müşteri "1 Ocak itibarıyla 224 iskele" diyor ve bu bir
   *    STOK sayısıdır — o gün sahada duranların adedi. Panodaki dönem seçici
   *    ise bir ARALIK süzer (o dönemde tamamlananlar); ikisi farklı soruyu
   *    cevaplar ve aynı sayıyı vermezler.
   */
  const [tarih, setTarih] = useState(BUGUN());
  const bugunMu = tarih === BUGUN();

  const { duzen, sonuc, sirala, siradanCikar, suzgecYaz, duzenYaz, temizle } = useTabloDuzeni(
    d?.isler ?? null,
    KOLONLAR,
    VARSAYILAN_SIRA,
  );

  // Derin bağlantı YALNIZ İLK AÇILIŞTA uygulanır; sonrasında kullanıcının
  // ekranda kurduğu düzeni ezmemeli.
  useEffect(() => {
    if (baslangicVade) duzenYaz(VADE_GORUNUMU[baslangicVade]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setD(null);
    api
      .get<{ toplamM3: number; isler: KuruluIs[] }>(`/kurulu?tarih=${tarih}`)
      .then(setD)
      .catch((e: Error) => setHata(e.message));
  }, [tarih]);

  if (hata) return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;

  const dugme = (aktif: boolean) =>
    `rounded-(--radius-mod) border px-3 py-1.5 text-sm ${
      aktif ? 'border-navy bg-navy text-white' : 'border-line-2 text-wait hover:border-navy hover:text-navy'
    }`;

  /** Süzgeç sonrası metraj — üstteki toplam süzgeci yansıtmalı, yoksa yanıltır. */
  const gorunenM3 = sonuc.reduce((a, j) => a + j.esdegerM3, 0);
  const suzuluyor = sonuc.length !== (d?.isler.length ?? 0);

  return (
    <div className="mx-auto max-w-6xl p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-navy">
            Sahada Kurulu İskeleler{' '}
            <span className="num text-sm font-normal text-wait">
              {d ? (suzuluyor ? `${sonuc.length}/${d.isler.length}` : d.isler.length) : '…'}
            </span>
          </h1>
          <p className="text-sm text-wait">
            {bugunMu ? (
              <>
                Teslim edilmiş, henüz sökülmemiş. Sütun başlığından süzün ve sıralayın —
                birden çok kolona göre sıralamak için <strong>Shift</strong> ile tıklayın.
              </>
            ) : (
              <>
                <strong>{new Date(`${tarih}T12:00:00`).toLocaleDateString('tr-TR')}</strong>{' '}
                tarihinde sahada kurulu olanlar — geçmişe dönük sayım.
              </>
            )}
          </p>
        </div>
        <span className="num rounded-(--radius-mod) bg-info/10 px-3 py-1.5 text-sm text-info">
          {(suzuluyor ? gorunenM3 : (d?.toplamM3 ?? 0)).toFixed(2).replace('.', ',')} m³
          {suzuluyor ? ' (süzülmüş)' : ' kurulu'}
        </span>
      </div>

      {/*
        TARİH SEÇİCİ — "şu tarihte kaç iskele kuruluydu".
        Yıl başı hazır seçim olarak duruyor: müşterinin devir bakiyesi
        ("1 Ocak itibarıyla 224") en sık sorulan sayı.
      */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-wait">Tarih:</span>
        <button onClick={() => setTarih(BUGUN())} className={dugme(bugunMu)}>
          Bugün
        </button>
        <button onClick={() => setTarih(YIL_BASI())} className={dugme(tarih === YIL_BASI())}>
          Yıl başı ({new Date().getFullYear()})
        </button>
        <input
          type="date"
          value={tarih}
          max={BUGUN()}
          onChange={(e) => e.target.value && setTarih(e.target.value)}
          aria-label="Sayım tarihi"
          className="num rounded-(--radius-mod) border border-line-2 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-navy"
        />
        {!bugunMu && (
          <span className="text-xs text-warn">
            Geçmiş sayım — “söküm aç” gibi işlemler için bugüne dönün.
          </span>
        )}
      </div>

      {!d && <p className="py-10 text-center text-sm text-wait">Yükleniyor…</p>}
      {d && (
        <>
          <VadePaneli
            isler={d.isler}
            aktif={aktifVade(duzen)}
            onSec={(g) => duzenYaz(g ? VADE_GORUNUMU[g] : { sira: VARSAYILAN_SIRA, suzgecler: {} })}
          />
          <DuzenOzeti
            kolonlar={KOLONLAR}
            duzen={duzen}
            toplam={d.isler.length}
            gorunen={sonuc.length}
            onTemizle={temizle}
            onSiradanCikar={siradanCikar}
            onSuzgecKaldir={(a) => suzgecYaz(a, null)}
          />

          {d.isler.length === 0 ? (
            <div className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-10 text-center">
              <p className="font-medium text-ink-2">Sahada kurulu iskele yok</p>
              <p className="mt-1 text-sm text-wait">Teslim edilen işler burada listelenir.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-wait">
                  <tr>
                    {KOLONLAR.map((k) => (
                      <KolonBasi
                        key={k.anahtar}
                        kolon={k}
                        veri={d.isler}
                        duzen={duzen}
                        sirala={sirala}
                        siradanCikar={siradanCikar}
                        suzgecYaz={suzgecYaz}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sonuc.map((j) => (
                    <tr
                      key={j.id}
                      onClick={() => onIsSec(j.id)}
                      className="cursor-pointer border-b border-line/70 last:border-0 hover:bg-ground"
                    >
                      <td className="num px-3 py-2 whitespace-nowrap">
                        {j.isEmriNo ?? '—'}
                        {!j.yesilKart && (
                          <span className="ml-2 rounded bg-crit/15 px-1.5 py-0.5 text-xs text-crit">
                            yeşil kart yok
                          </span>
                        )}
                      </td>
                      {/* Aynı iş emrindeki farklı iskeleleri ayıran numara. */}
                      <td className="num px-3 py-2 whitespace-nowrap text-wait">
                        {j.iskeleOnayNo ?? '—'}
                      </td>
                      <td className="px-3 py-2">{j.tanim}</td>
                      <td className="px-3 py-2 text-ink-2">{j.unite}</td>
                      <td className="num px-3 py-2 text-right">{metrajGoster(j)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {/* 40+ gün uyarı rengiyle (Bölüm 6.5) */}
                        <span
                          className={
                            j.uzunSure
                              ? 'num rounded bg-warn/15 px-2 py-0.5 text-xs text-warn'
                              : 'num text-wait'
                          }
                          title={gunTarih(j.kurulumTarihi) ?? undefined}
                        >
                          {j.kuruluGun ?? '—'} gün
                        </span>
                      </td>
                      {/*
                        HAFTALIK KONTROL — İKİ AYRI BİLGİ, TEK HÜCRE.
                          renk  → VADE (güncel / sarı / kırmızı)
                          metin → OLAY (kontrol edildi mi, ne zaman)
                        Eskiden ikisi birbirine karışıyordu: hiç kontrol
                        edilmemiş her iskele, dün teslim edilmiş olsa bile
                        kırmızı yanıyordu.
                      */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`num ${VADE_RENGI[j.kontrolDurumu]}`}
                          title={
                            j.hicKontrolYapilmadi
                              ? `Henüz haftalık kontrol yapılmadı — sayaç teslimden (${gunTarih(j.kurulumTarihi) ?? '—'}) işliyor`
                              : `Son kontrol: ${gunTarih(j.sonKontrolTarihi) ?? '—'}`
                          }
                        >
                          {j.hicKontrolYapilmadi
                            ? `${j.vadeGun ?? '—'} gündür (hiç yapılmadı)`
                            : `${j.sonKontrolGun} gün önce`}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-wait">{j.ilgili[0] ?? '—'}</td>
                    </tr>
                  ))}
                  {sonuc.length === 0 && (
                    <tr>
                      <td colSpan={KOLONLAR.length} className="px-3 py-10 text-center text-sm text-wait">
                        Süzgeçlere uyan iskele yok.{' '}
                        <button onClick={temizle} className="text-navy underline">
                          Süzgeçleri temizle
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-wait">
            Haftalık kontrol <strong>{KONTROL_PERIYOT_GUN} günde bir</strong> yapılır.{' '}
            <span className="text-warn">Sarı</span> {KONTROL_SARI_GUN}. gün,{' '}
            <span className="text-crit">kırmızı</span> {KONTROL_PERIYOT_GUN}. günden itibaren.
            Hiç kontrol edilmemiş iskelede sayaç <strong>teslim tarihinden</strong> işler — yeni
            teslim edilmiş bir iskele vadesi geçmiş sayılmaz. Gün sayılarının üzerine gelince
            tarih görünür.
          </p>
        </>
      )}
    </div>
  );
}
